import { Button, Card, Group, Text, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { StepSection } from '@/components/deployments/deployment-editor/StepSection'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { CameraSource, CameraStreamWithDeviceId } from '@/types/camera'
import {
  EventDestination,
  EventEndpoint,
  InputSourceType,
  MqttAttributes
} from '@/types/deployment'
import { isValidUrl } from '@/utils/validation/url'
import { MilestoneIntegration } from './integrations/MilestoneIntegration'
import { PlatformIntegration } from './integrations/PlatformIntegration'
import { RestApiIntegration } from './integrations/RestApiIntegration'
import { MqttIntegration } from './integrations/mqtt/MqttIntegration'

export type FormValues = {
  integrations: {
    platformEvents: {
      isSelected: boolean
    }
    restApi: {
      isSelected: boolean
      endpointUrl: string
    }
    milestone: {
      isSelected: boolean
      endpointUrl: string
    }
    mqtt: {
      isSelected: boolean
      attributes: MqttAttributes
    }
  }
}

type IntegrationSelectionProps = {
  selectedDeviceIds: string[]
  selectedCameraStreams: CameraStreamWithDeviceId[]
  savedEventEndpoints: EventEndpoint[]
  inputSource: InputSourceType
  onGoBack: () => void
  onContinue: (values: EventEndpoint[]) => void
}

export const IntegrationSelection = ({
  selectedDeviceIds,
  selectedCameraStreams,
  savedEventEndpoints,
  inputSource,
  onGoBack,
  onContinue
}: IntegrationSelectionProps) => {
  const isMediaFolder = inputSource === InputSourceType.MediaFolder
  const intl = useIntl()

  const hasMilestoneCameraSelected = useMemo(
    () =>
      selectedCameraStreams.some(
        (camera) => camera.source === CameraSource.Milestone
      ),
    [selectedCameraStreams]
  )

  const form = useForm<FormValues>({
    initialValues: {
      integrations: {
        platformEvents: {
          isSelected: savedEventEndpoints.some(
            (endpoint) => endpoint.service === EventDestination.Visionplatform
          )
        },
        restApi: {
          isSelected: savedEventEndpoints.some(
            (endpoint) => endpoint.service === EventDestination.CustomApi
          ),
          endpointUrl:
            savedEventEndpoints.find(
              (endpoint) => endpoint.service === EventDestination.CustomApi
            )?.endpoint || ''
        },
        milestone: {
          isSelected: savedEventEndpoints.some(
            (endpoint) => endpoint.service === EventDestination.Milestone
          ),
          endpointUrl:
            savedEventEndpoints.find(
              (endpoint) => endpoint.service === EventDestination.Milestone
            )?.endpoint || ''
        },
        mqtt: {
          isSelected: savedEventEndpoints.some(
            (endpoint) => endpoint.service === EventDestination.Mqtt
          ),
          attributes: (savedEventEndpoints.find(
            (endpoint) => endpoint.service === EventDestination.Mqtt
          )?.attributes as MqttAttributes) || {
            broker_address: 'localhost',
            broker_port: 18831,
            broker_qos: 0,
            topics: []
          }
        }
      }
    },

    validateInputOnBlur: true,

    validate: {
      integrations: {
        restApi: {
          endpointUrl: (value, values) => {
            if (values.integrations.restApi.isSelected && !isValidUrl(value)) {
              return intl.formatMessage({
                id: 'validation.url.invalid'
              })
            }

            return null
          }
        },
        milestone: {
          endpointUrl: (value, values) => {
            if (!values.integrations.milestone.isSelected) {
              return null
            }

            if (!isValidUrl(value)) {
              return intl.formatMessage({
                id: 'validation.url.invalid'
              })
            }

            if (!hasMilestoneCameraSelected) {
              return intl.formatMessage({
                id: 'integrations.milestone.camera.required'
              })
            }

            return null
          }
        },
        mqtt: {
          attributes: (value, values) => {
            if (!values.integrations.mqtt.isSelected) {
              return null
            }

            if (value.broker_address === '') {
              return intl.formatMessage({
                id: 'integrations.mqtt.broker.url.required'
              })
            }

            if (value.topics.length === 0) {
              return intl.formatMessage({
                id: 'integrations.mqtt.topics.required'
              })
            }

            return null
          }
        }
      }
    }
  })

  const formValues = form.getValues()

  const handleContinueClick = () => {
    const submitIntegrations = [
      formValues.integrations.platformEvents.isSelected && {
        service: EventDestination.Visionplatform
      },
      formValues.integrations.restApi.isSelected && {
        service: EventDestination.CustomApi,
        endpoint: formValues.integrations.restApi.endpointUrl
      },
      formValues.integrations.milestone.isSelected && {
        service: EventDestination.Milestone,
        endpoint: formValues.integrations.milestone.endpointUrl
      },
      formValues.integrations.mqtt.isSelected && {
        service: EventDestination.Mqtt,
        attributes: formValues.integrations.mqtt.attributes
      }
    ].filter(Boolean) as EventEndpoint[]

    onContinue(submitIntegrations)
  }

  return (
    <>
      <Title order={3} mb="md">
        <FormattedMessage id="deployments.step6" />
      </Title>

      <StepSection
        actions={
          <Group>
            <Button
              size="md"
              variant="outline"
              miw={200}
              leftSection={<IconChevronLeft size={16} stroke={3} />}
              onClick={onGoBack}
            >
              <FormattedMessage id="deployments.previousStep" />
            </Button>

            <Button
              miw={200}
              size="md"
              rightSection={<IconChevronRight size={16} stroke={3} />}
              disabled={!form.isValid()}
              onClick={handleContinueClick}
            >
              <FormattedMessage id="deployments.nextStep" />
            </Button>
          </Group>
        }
      >
        {isMediaFolder && (
          <PlatformAlert variant="info" mb="xl">
            <FormattedMessage id="integrations.cameraOnly" />
          </PlatformAlert>
        )}

        <Card p="lg">
          <Card.Section py="sm" inheritPadding>
            <Text size="sm">
              <FormattedMessage id="integrations.notificationPreference" />
            </Text>
          </Card.Section>

          <Card.Section py="lg" inheritPadding withBorder>
            <PlatformIntegration form={form} disabled={isMediaFolder} />
          </Card.Section>

          <Card.Section py="lg" inheritPadding withBorder>
            <RestApiIntegration
              deviceId={selectedDeviceIds[0]} // TODO we have a task to validate URL for multiple devices
              form={form}
              disabled={isMediaFolder}
            />
          </Card.Section>

          <Card.Section py="lg" inheritPadding withBorder>
            <MilestoneIntegration
              disabled={isMediaFolder}
              hasMilestoneCameraSelected={hasMilestoneCameraSelected}
              deviceId={selectedDeviceIds[0]}
              form={form}
            />
          </Card.Section>

          <Card.Section py="lg" inheritPadding withBorder>
            <MqttIntegration
              deviceId={selectedDeviceIds[0]}
              form={form}
              disabled={isMediaFolder}
            />
          </Card.Section>
        </Card>
      </StepSection>
    </>
  )
}
